import { ApiError } from '@peritasai/data-access';
import {
  CaseEditingSessionEntity,
  caseScheduleRepository,
} from '@peritasai/repository';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../../auth/stores/auth.store';

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes keeps us well inside the 5 minute timeout

export interface UseCaseEditingSessionResult {
  session: CaseEditingSessionEntity | null;
  isStarting: boolean;
  error: string | null;
  isLockedByAnotherUser: boolean;
  stopSession: () => Promise<void>;
}

interface UseCaseEditingSessionOptions {
  onSessionEnd?: () => Promise<void> | void;
  onSessionStart?: () => Promise<void> | void;
}

type IntervalId = ReturnType<typeof setInterval>;

export function useCaseEditingSession(
  caseId?: string,
  isActive?: boolean,
  options?: UseCaseEditingSessionOptions
): UseCaseEditingSessionResult {
  const [session, setSession] = useState<CaseEditingSessionEntity | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLockedByAnotherUser, setIsLockedByAnotherUser] = useState(false);
  const heartbeatRef = useRef<IntervalId | null>(null);
  const sessionRef = useRef<CaseEditingSessionEntity | null>(null);
  const startingRef = useRef<boolean>(false); // Add this to track in-flight starts
  const endingRef = useRef<boolean>(false); // Add this to track in-flight ends
  const processingCaseIdRef = useRef<string | null>(null); // Track which caseId we're currently processing
  const onSessionEnd = options?.onSessionEnd;
  const onSessionStart = options?.onSessionStart;
  const currentUserId = useAuthStore((state) => state.user?.id);
  const isAuthInitialized = useAuthStore((state) => state.isInitialized);

  const clearHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const stopSession = useCallback(async () => {
    console.log('[stopSession] Called', {
      hasSession: !!sessionRef.current,
      caseId: sessionRef.current?.caseId,
      endingRef: endingRef.current,
    });

    // Prevent concurrent end attempts
    if (endingRef.current) {
      console.log('[stopSession] Already ending session, skipping');
      return;
    }

    // Capture caseId from sessionRef BEFORE clearing anything
    const currentSession = sessionRef.current;
    const targetCaseId = currentSession?.caseId;

    // Clear ALL local session data immediately
    clearHeartbeat();
    sessionRef.current = null;
    setSession(null);

    // Only proceed if we have a valid caseId to end
    if (!targetCaseId) {
      console.log('[stopSession] No active session, nothing to end');
      return;
    }

    console.log('[stopSession] Ending session for caseId:', targetCaseId);
    endingRef.current = true;

    try {
      // Parallelize session end operations: run onSessionEnd callback and endEditingSession API call concurrently
      // This reduces session end time from 2 sequential calls to 1 parallel batch
      const operations: Promise<void>[] = [];

      if (onSessionEnd) {
        operations.push(
          Promise.resolve(onSessionEnd())
            .then(() => {
              console.log('[stopSession] onSessionEnd callback completed');
            })
            .catch((error) => {
              console.error('[stopSession] Failed to finalize session state', error);
              // Don't throw - we still want to end the session even if finalization fails
            })
        );
      }

      operations.push(
        caseScheduleRepository
          .endEditingSession(targetCaseId)
          .then(() => {
            console.log('[stopSession] API call succeeded for caseId:', targetCaseId);
          })
          .catch((error) => {
            // If we get a 409, the session might already be ended - that's fine
            if (
              error instanceof Error &&
              (error.message?.includes('409') ||
                error.message?.includes('Conflict'))
            ) {
              console.log('[stopSession] Session already ended (409), ignoring');
            } else {
              console.warn('[stopSession] API call failed:', error);
            }
            // Don't throw - we've logged the error, allow Promise.allSettled to complete
          })
      );

      // Use Promise.allSettled to ensure both operations complete even if one fails
      await Promise.allSettled(operations);
      console.log('[stopSession] All operations completed for caseId:', targetCaseId);
    } catch (error) {
      // This catch block should rarely be hit since we're using allSettled,
      // but keep it for safety
      console.warn('[stopSession] Unexpected error during session end operations', error);
    } finally {
      endingRef.current = false;
      console.log('[stopSession] Completed for caseId:', targetCaseId);
    }
  }, [clearHeartbeat, onSessionEnd]);

  const scheduleHeartbeat = useCallback(
    (targetCaseId: string) => {
      clearHeartbeat();
      heartbeatRef.current = setInterval(() => {
        caseScheduleRepository
          .extendEditingSession(targetCaseId)
          .then((entity) => {
            sessionRef.current = entity;
            setSession(entity);
          })
          .catch((error) => {
            console.error('Failed to extend editing session', error);
            void stopSession();
          });
      }, HEARTBEAT_INTERVAL_MS);
    },
    [clearHeartbeat, stopSession]
  );

  // Helper function to call onSessionStart callback safely
  const callOnSessionStart = useCallback(async () => {
    if (onSessionStart) {
      try {
        await Promise.resolve(onSessionStart());
      } catch (error) {
        console.error('Failed to execute onSessionStart callback', error);
        // Don't throw - session start should succeed even if callback fails
      }
    }
  }, [onSessionStart]);

  useEffect(() => {
    return () => {
      clearHeartbeat();
    };
  }, [clearHeartbeat]);

  useEffect(() => {
    console.log('[useCaseEditingSession] Effect triggered', {
      caseId,
      isActive,
      isAuthInitialized,
      currentUserId,
      isStarting: startingRef.current,
      processingCaseId: processingCaseIdRef.current,
    });

    // Early returns for invalid state
    if (!caseId || !isActive || !isAuthInitialized) {
      console.log(
        '[useCaseEditingSession] Early return: no caseId, not active, or auth not initialized'
      );
      // Clear session if caseId changes or becomes inactive
      if (sessionRef.current && sessionRef.current.caseId !== caseId) {
        console.log(
          '[useCaseEditingSession] CaseId changed, ending previous session'
        );
        void stopSession();
      }
      return;
    }

    // Prevent concurrent starts
    if (startingRef.current && processingCaseIdRef.current === caseId) {
      console.log(
        '[useCaseEditingSession] Already processing this caseId, skipping'
      );
      return;
    }

    // Always check server first - no local caching
    let cancelled = false;
    startingRef.current = true;
    processingCaseIdRef.current = caseId;
    setIsStarting(true);
    setError(null);
    setIsLockedByAnotherUser(false);

    console.log(
      '[useCaseEditingSession] Checking server for existing session first'
    );

    caseScheduleRepository
      .getEditingSession(caseId)
      .then((serverSession) => {
        if (cancelled) {
          startingRef.current = false;
          processingCaseIdRef.current = null;
          return;
        }

        if (serverSession) {
          console.log('[useCaseEditingSession] Server has session:', {
            sessionUserId: serverSession.userId,
            currentUserId,
            isExpired: serverSession.isExpired,
          });

          // Server has session - verify ownership
          if (currentUserId && serverSession.userId !== currentUserId) {
            console.log(
              '[useCaseEditingSession] Session locked by another user',
              {
                sessionUserId: serverSession.userId,
                currentUserId,
              }
            );
            setIsLockedByAnotherUser(true);
            setError(
              `This case is currently being edited by ${
                serverSession.userInitials || 'another user'
              }`
            );
            setIsStarting(false);
            startingRef.current = false;
            processingCaseIdRef.current = null;
            return;
          }

          // Use server session (belongs to current user)
          console.log(
            '[useCaseEditingSession] Using existing server session'
          );
          sessionRef.current = serverSession;
          setSession(serverSession);
          void callOnSessionStart();
          scheduleHeartbeat(caseId);
          setIsStarting(false);
          startingRef.current = false;
          processingCaseIdRef.current = null;
          return;
        }

        // No server session - start new one
        console.log(
          '[useCaseEditingSession] No server session found, starting new session'
        );
        return caseScheduleRepository.startEditingSession(caseId);
      })
      .catch((error) => {
        if (cancelled) {
          startingRef.current = false;
          processingCaseIdRef.current = null;
          return;
        }

        // Handle locked by another user error from getEditingSession
        if (error instanceof ApiError && error.isLockedByAnotherUser) {
          const lockedBy = error.lockedBy;
          console.log(
            '[useCaseEditingSession] Session locked by another user:',
            lockedBy
          );
          setIsLockedByAnotherUser(true);
          setError(
            `This case is currently being edited by ${
              lockedBy?.userInitials || lockedBy?.userId || 'another user'
            }`
          );
          setIsStarting(false);
          startingRef.current = false;
          processingCaseIdRef.current = null;
          return;
        }

        // If getEditingSession fails for other reasons, try starting anyway
        console.log(
          '[useCaseEditingSession] getEditingSession failed, attempting to start new session:',
          error.message
        );
        return caseScheduleRepository.startEditingSession(caseId);
      })
      .then((newSession) => {
        if (cancelled || !newSession) {
          startingRef.current = false;
          processingCaseIdRef.current = null;
          return;
        }

        console.log('[useCaseEditingSession] Session started successfully:', {
          caseId: newSession.caseId,
          userId: newSession.userId,
        });
        sessionRef.current = newSession;
        setSession(newSession);
        void callOnSessionStart();
        scheduleHeartbeat(caseId);
        setIsStarting(false);
        startingRef.current = false;
        processingCaseIdRef.current = null;
      })
      .catch((error) => {
        if (cancelled) {
          startingRef.current = false;
          processingCaseIdRef.current = null;
          return;
        }

        console.error('[useCaseEditingSession] Error starting session:', error);

        // Handle locked by another user error from startEditingSession
        if (error instanceof ApiError && error.isLockedByAnotherUser) {
          const lockedBy = error.lockedBy;
          console.log(
            '[useCaseEditingSession] Session locked by another user:',
            lockedBy
          );
          setIsLockedByAnotherUser(true);
          setError(
            `This case is currently being edited by ${
              lockedBy?.userInitials || lockedBy?.userId || 'another user'
            }`
          );
          setIsStarting(false);
          startingRef.current = false;
          processingCaseIdRef.current = null;
          return;
        }

        // Handle 409 conflict
        if (
          error.message?.includes('409') ||
          error.message?.includes('Conflict')
        ) {
          console.log(
            '[useCaseEditingSession] Got 409 conflict, checking server status'
          );
          // Check server status to see actual state
          return caseScheduleRepository
            .getEditingSession(caseId)
            .then((statusSession) => {
              if (cancelled) {
                startingRef.current = false;
                processingCaseIdRef.current = null;
                return;
              }

              if (statusSession && statusSession.userId === currentUserId) {
                // Use existing session
                console.log(
                  '[useCaseEditingSession] Using existing session after 409'
                );
                sessionRef.current = statusSession;
                setSession(statusSession);
                void callOnSessionStart();
                scheduleHeartbeat(caseId);
                setIsStarting(false);
                startingRef.current = false;
                processingCaseIdRef.current = null;
                return;
              }

              // Check if session belongs to another user
              if (statusSession && statusSession.userId !== currentUserId) {
                console.log(
                  '[useCaseEditingSession] Session locked by another user after 409',
                  {
                    sessionUserId: statusSession.userId,
                    currentUserId,
                  }
                );
                setIsLockedByAnotherUser(true);
                setError(
                  `This case is currently being edited by ${
                    statusSession.userInitials || 'another user'
                  }`
                );
                setIsStarting(false);
                startingRef.current = false;
                processingCaseIdRef.current = null;
                return;
              }

              // Status shows inactive - retry start after brief delay
              console.log(
                '[useCaseEditingSession] Status shows inactive, retrying start after delay'
              );
              return new Promise((resolve) => setTimeout(resolve, 100))
                .then(() => caseScheduleRepository.startEditingSession(caseId));
            })
            .catch((statusError) => {
              // If status check also fails, show error
              console.error(
                '[useCaseEditingSession] Error checking status after 409:',
                statusError
              );
              setError(
                statusError instanceof Error
                  ? statusError.message
                  : 'Failed to start editing session'
              );
              setIsStarting(false);
              startingRef.current = false;
              processingCaseIdRef.current = null;
            })
            .then((retrySession) => {
              if (cancelled || !retrySession) {
                startingRef.current = false;
                processingCaseIdRef.current = null;
                return;
              }

              console.log(
                '[useCaseEditingSession] Session started successfully after retry'
              );
              sessionRef.current = retrySession;
              setSession(retrySession);
              void callOnSessionStart();
              scheduleHeartbeat(caseId);
              setIsStarting(false);
              startingRef.current = false;
              processingCaseIdRef.current = null;
            });
        }

        // Other errors
        console.error('Unable to start editing session', error);
        setError(
          error instanceof Error
            ? error.message
            : 'Failed to start editing session'
        );
        setIsStarting(false);
        startingRef.current = false;
        processingCaseIdRef.current = null;
      });

    return () => {
      cancelled = true;
      startingRef.current = false;
      processingCaseIdRef.current = null;
    };
  }, [
    caseId,
    isActive,
    isAuthInitialized,
    currentUserId,
    scheduleHeartbeat,
    stopSession,
    callOnSessionStart,
  ]);

  useEffect(() => {
    if (!session) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      const activeSession = sessionRef.current;
      if (!activeSession) {
        return;
      }

      if (onSessionEnd) {
        try {
          void onSessionEnd();
        } catch (error) {
          console.error('Failed to finalize session state on unload', error);
        }
      }

      // Fire-and-forget — the browser may terminate the request early, but this is best-effort.
      if (activeSession.caseId) {
        void caseScheduleRepository.endEditingSession(activeSession.caseId);
      }

      // Trigger native confirmation dialog
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [session, onSessionEnd]);

  useEffect(() => {
    return () => {
      // Only end session if we have an active session
      if (sessionRef.current) {
        void stopSession();
      }
    };
  }, [stopSession]);

  return {
    session,
    isStarting,
    error,
    isLockedByAnotherUser,
    stopSession,
  };
}
